import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { waLink } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";

export const CruiseSection = ({ cruises }) => {
  const { t } = useLang();
  const c = cruises;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="cruises" ref={ref} className="relative bg-navy py-24 md:py-32 overflow-hidden" data-testid="cruises">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <Overline className="text-goldsoft">{c.overline}</Overline>
              <h2 className="mt-4 font-serif font-light text-canvas text-4xl md:text-6xl tracking-tight">{c.title}</h2>
              <p className="mt-6 max-w-lg font-light leading-relaxed text-canvas/70">{c.desc}</p>

              <p className="mt-10 text-[10px] uppercase tracking-[0.22em] text-canvas/50">{c.destinationsLabel}</p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="cruise-destinations">
                {c.destinations.map((d) => (
                  <div key={d.name} className="group relative overflow-hidden border border-goldsoft/30">
                    <img
                      src={d.img}
                      alt={d.name}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-navy/35 transition-colors group-hover:bg-navy/20" />
                    <span className="absolute bottom-2 left-3 font-serif text-lg text-canvas drop-shadow">{d.name}</span>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-[10px] uppercase tracking-[0.22em] text-canvas/50">{c.boardsLabel}</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-px border border-goldsoft/30 bg-canvas/15" data-testid="cruise-boards">
                {c.boards.map((b) => (
                  <div key={b.name} className="bg-navy p-5">
                    <p className="font-serif text-xl text-canvas">{b.name}</p>
                    <p className="mt-1 text-sm font-light text-canvas/65">{b.desc}</p>
                  </div>
                ))}
              </div>

              <a
                href={waLink(`${t.waMsg} (${c.title})`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cruise-book-button"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-canvas px-7 py-4 text-navy transition-colors hover:bg-gold hover:text-navy"
              >
                <span className="text-sm uppercase tracking-[0.15em] font-medium">{c.cta}</span>
                <span>→</span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal delay={0.1} className="overflow-hidden border border-canvas/10">
              <motion.img
                style={{ y }}
                src={c.img}
                alt={c.title}
                className="aspect-[4/5] md:aspect-[3/4] w-full object-cover"
              />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
